import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Checkbox, TextField as MuiTextField } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import CheckIcon from '@material-ui/icons/Check';
import { useFormikContext, Field } from 'formik';
import { useMutation, useLazyQuery } from '@apollo/react-hooks';
import { Alert } from 'antd';
import WarningModal from '../../../../components/WarningModal/WarningModal';
import PaymentSection from '../../shared/PaymentSection';
import FormCard from '../../../../components/Cards/FormCard';
import { HeadingTwo } from '../../../../components/Headings';
import OSTooltip from '../../../../components/Tooltip/OSTooltip';
import { FormikField } from '../../../../components/Fields';
import Button from '../../../../components/Button';
import colors from '../../../../styles/Colors';
import { VALIDATE_CODE } from '../../../../mutations/ValidateCode';
import { GROUP_BY_UNIQUE_TEXT } from '../../../../mutations/GetGroupByUniqueText';
import { ORDER_CREATE_COSTS } from 'Queries/OrderCreateCosts';
import { buildOrderItems } from '../../../../helpers';

const ERROR_MESSAGE = 'Invalid group code; please check and try again';

function PaymentDetailsBase(props) {
  const TOOLTIP_TEXT =
    props.isGroupCodeRequired === false
      ? 'Select box to settle your payment later. Credit/Debit card info needs to be saved to use this feature.'
      : 'Select box if you are approved by the Group Leader or Venue Admin to defer your payment to the group bill';
  const [newCard, setNewCard] = useState(false);
  const [delayPayment, setDelayPayment] = useState(false);
  const [code, setCode] = useState(null);
  const [groupSelected, setGroupSelected] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountCodeApplied, setDiscountCodeApplied] = useState(null);
  const [discountCodeError, setDiscountCodeError] = useState(null);
  const { setFieldValue, values } = useFormikContext();
  const [validateCode, { data }] = useMutation(VALIDATE_CODE);
  const [groupUniqueText, { data: groupData }] = useMutation(GROUP_BY_UNIQUE_TEXT);
  const [getOrderCosts, { data: orderCostsData }] = useLazyQuery(ORDER_CREATE_COSTS, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (data) {
      if (!data.group) {
        setGroupSelected(null);
        setFieldValue('ccInformation.groupCode', false);
        return setErrorCode(ERROR_MESSAGE);
      }

      setGroupSelected(data.group);
      setCode(code);
      setFieldValue('ccInformation.groupCode', true);
      setErrorCode(null);
    }
  }, [data]);

  useEffect(() => {
    if (groupData) {
      if (!groupData.group) {
        setGroupSelected(null);
        return setErrorCode(ERROR_MESSAGE);
      }

      setGroupSelected(groupData.group);
      setCode(groupData.group.code);
      setErrorCode(null);
    }
  }, [groupData]);

  useEffect(() => {
    if (delayPayment) {
      setFieldValue('ccInformation.useCard', true);
      clearGroupInformation();
      if (props.isGroupCodeRequired === false) {
        groupUniqueText({
          variables: {
            input: {
              name: props.event.name,
              venueId: props.event.venue.id
            }
          }
        });
      } else {
        return clearCardInformation();
      }
    } else {
      setFieldValue('ccInformation.useCard', true);
    }

    setGroupSelected(null);
  }, [delayPayment]);

  useEffect(() => {
    if (groupSelected) {
      setGroupInformation();
    } else {
      clearGroupInformation();
      clearCardInformation();
    }
  }, [groupSelected, code]);

  const clearCardInformation = () => {
    setFieldValue('ccInformation.selectedCard', null);
    setFieldValue('ccInformation.selectedCardBrand', null);
    setFieldValue('ccInformation.country', null);
  };

  const setGroupInformation = () => {
    setFieldValue('group.id', groupSelected.id);
    setFieldValue('group.code', code);
  };

  const clearGroupInformation = () => {
    setFieldValue('group', null);
  };

  const handleDelayPayment = () => {
    if (delayPayment) {
      setFieldValue('ccInformation.useCard', false);
      setDelayPayment(!delayPayment);
    } else if (modalShown === false && props.isGroupCodeRequired === false) setShowDelayModal(true);
    else if (props.isGroupCodeRequired) {
      setDelayPayment(true);
      setFieldValue('ccInformation', {
        nameOnCard: null,
        saveCard: true,
        selectedCard: null,
        zipCode: null,
        useCard: true,
        country: ''
      });
    } else {
      setDelayPayment(true);
      setFieldValue('ccInformation.useCard', true);
    }
  };

  const handleValidateCode = code => {
    setCode(code);
    validateCode({
      variables: {
        input: {
          code,
          eventVenueId: props.event.venue.id
        }
      }
    });
  };

  const handleOnGoBack = () => setShowDelayModal(false);

  const handleOnAgreeDelayed = () => {
    setDelayPayment(true);
    setModalShown(true);
    setShowDelayModal(false);
  };

  const handleApplyDiscountCode = async () => {
    if (!discountCodeInput.trim()) return;

    try {
      const orderItemsArray = buildOrderItems(values);
      await getOrderCosts({
        variables: {
          input: {
            selectedOrderItems: orderItemsArray,
            useCard: !!values.ccInformation?.useCard,
            cardReader: !!values.ccInformation?.cardReaderPayment,
            isNonUSCard: false,
            discountCode: discountCodeInput.trim()
          }
        }
      });
    } catch (error) {
      setDiscountCodeError('Failed to validate discount code');
      setDiscountCodeApplied(null);
    }
  };

  // Handle the response from getOrderCosts
  useEffect(() => {
    if (orderCostsData?.orderCosts) {
      const orderCosts = orderCostsData.orderCosts;

      if (orderCosts?.discountCodeError) {
        setDiscountCodeError(orderCosts.discountCodeError);
        setDiscountCodeApplied(null);
        setFieldValue('discountCode', null);
      } else if (orderCosts?.discountCodeApplied) {
        setDiscountCodeApplied(orderCosts.discountCodeApplied);
        setDiscountCodeError(null);
        setFieldValue('discountCode', orderCosts.discountCodeApplied);
        setDiscountCodeInput('');
      }
    }
  }, [orderCostsData]);

  const handleRemoveDiscountCode = () => {
    setDiscountCodeApplied(null);
    setDiscountCodeError(null);
    setDiscountCodeInput('');
    setFieldValue('discountCode', null);
  };

  return (
    <FormCard className={`${props.className}__renter_payment_details`} dataTestId="renter_payment_details">
      <div className={`${props.className}__containerDiv`}>
        <div className={`${props.className}__headerDiv`}>
          <HeadingTwo label={`Payment Details`} />
        </div>
        <WarningModal
          isOpen={showDelayModal}
          onCancel={handleOnGoBack}
          continueLabel="AGREE"
          cancelLabel="GO BACK"
          header=""
          hideWarningIcon={true}
          text="In order to delay your payment, your credit card information will be saved. You won't be charged until later."
          onContinue={handleOnAgreeDelayed}
        />
        {(!delayPayment || (delayPayment && props.isGroupCodeRequired === false)) && <PaymentSection {...props} setNewCard={setNewCard} newCard={newCard} />}

        {props.event?.venue?.discountCodesEnabled && props.event?.venue?.discountCodesForRenters && (
          <div className={`${props.className}__discount-code-section`}>
            <DiscountCodeTitle>Discount Code</DiscountCodeTitle>
            {!discountCodeApplied ? (
              <>
                <DiscountCodeInputContainer>
                  <MuiTextField
                    variant="outlined"
                    placeholder="Enter discount code"
                    value={discountCodeInput}
                    onChange={e => setDiscountCodeInput(e.target.value.toUpperCase())}
                    size="small"
                    fullWidth
                    style={{ marginRight: 8 }}
                    inputProps={{ 'data-testid': 'renter-discount-code-input' }}
                  />
                  <Button primary onClick={handleApplyDiscountCode} disabled={!discountCodeInput.trim()} data-testid="renter-discount-code-apply">
                    Apply
                  </Button>
                </DiscountCodeInputContainer>
                {discountCodeError && (
                  <Alert message={discountCodeError} type="error" showIcon style={{ marginTop: 12 }} closable onClose={() => setDiscountCodeError(null)} />
                )}
              </>
            ) : (
              <>
                <DiscountCodeInputContainer>
                  <MuiTextField variant="outlined" value={discountCodeApplied} size="small" fullWidth disabled style={{ marginRight: 8 }} />
                  <Button secondary onClick={handleRemoveDiscountCode} data-testid="renter-discount-code-remove">
                    Remove
                  </Button>
                </DiscountCodeInputContainer>
                <Alert
                  message={`Discount code "${discountCodeApplied}" applied successfully!`}
                  type="success"
                  showIcon
                  style={{ marginTop: 12 }}
                  closable={false}
                />
              </>
            )}
          </div>
        )}

        {props.isGroupCodeRequired != null && (
          <div className={`${props.className}__group-bill`}>
            <span>Pay Later</span>
            <StyleCheckbox data-testid="group-code-checkbox" onChange={handleDelayPayment} color="primary" checked={delayPayment} />
            <OSTooltip arrow title={TOOLTIP_TEXT} placement="top">
              <InfoIcon />
            </OSTooltip>
          </div>
        )}

        {delayPayment && props.isGroupCodeRequired && (
          <div className={`${props.className}__group-code`}>
            <p>Enter the unique group code tie to your payment to that group</p>
            <div className={`${props.className}__group-code-field`}>
              <Field
                data-testid="group-code-input"
                error={errorCode}
                helperText={errorCode}
                component={FormikField}
                label="GROUP CODE"
                name={'group.code'}
                type="text"
                variant="filled"
                required
                onChange={e => handleValidateCode(e.target.value)}
                style={{ width: '63%' }}
              />
            </div>
            {groupSelected && (
              <div data-testid="group-code-success" className={`${props.className}__group-code-field-confirm`}>
                <p>Your payment is connected to:</p>
                <p>
                  {groupSelected.name} <StyledCheckIcon />
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </FormCard>
  );
}

const PaymentDetails = styled(PaymentDetailsBase)`
  &__renter_payment_details {
    overflow: visible !important;
  }

  &__containerDiv {
    text-align: center;
  }

  &__headerDiv {
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__group-bill {
    display: flex;
    align-items: center;

    span {
      color: ${colors.text.primary};
      font-size: 18px;
    }
  }

  &__group-code {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    p {
      margin-bottom: 0;
    }

    &-field {
      display: flex;
      align-items: end;
      width: 100%;

      &&& div {
        margin-bottom: 0;
        margin-right: 10px;
      }

      &-confirm {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        &&& p:nth-child(1) {
          text-align: initial;
          font-size: 10px;
          width: 200px;
          line-height: 10px;
        }

        &&& p:nth-child(2) {
          margin-top: 0;
          text-transform: uppercase;
          display: flex;
        }
      }
    }
  }

  &__discount-code-section {
    margin: 20px 0;
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 4px;
  }
`;

const InfoIcon = styled(ErrorIcon)`
  &&& {
    cursor: pointer;
  }
`;

const StyledCheckIcon = styled(CheckIcon)`
  &&& {
    color: ${colors.primary};
    margin-left: 5px;
  }
`;

const StyleCheckbox = styled(Checkbox)`
  &&& {
    span svg {
      color: ${colors.secondary};
    }
  }
`;

const DiscountCodeTitle = styled.div`
  font-family: 'IBMPlexSans-SemiBold';
  font-size: 16px;
  margin-bottom: 12px;
  color: ${colors.text.primary};
`;

const DiscountCodeInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .MuiTextField-root {
    flex: 1;
  }

  button {
    white-space: nowrap;
    min-width: 80px;
    height: 40px;
  }
`;

export default PaymentDetails;
